import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./payments.css";
import Navbar from "../../../Shared/Sales-ExecutiveNavbar/Navbar";
import { ThemeContext } from "../../../Shared/Themes/ThemeContext";
import { baseURL } from "../../../Apiservices/Api";
import { AuthContext } from '../../../AuthContext/AuthContext';

const S_AddPaymentPage = () => {
  const { userId, userName } = useContext(AuthContext);
  const { leadid, paymentid } = useParams();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const { themeColor } = useContext(ThemeContext);

  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    paidAmount: "",
    balanceAmount: "",
    paidOn: today,
    remarks: "",
  });

  const [totalAmount, setTotalAmount] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchLeadAndPayments = async () => {
      try {
        setLoading(true);

        // Fetch travel opportunity to get total amount
        const travelRes = await fetch(`${baseURL}/travel-opportunity`);
        const travelData = await travelRes.json();
        const opportunity = travelData.find((item) => item.leadid == leadid);
        setTotalAmount(opportunity ? opportunity.approx_budget : 0);

        // Fetch all payments for this lead
        const paymentsRes = await fetch(`${baseURL}/api/payments?leadid=${leadid}`);
        let paymentsData = await paymentsRes.json();

        // Sort payments by paid_on (descending) and then by created_at (descending)
        paymentsData = paymentsData.sort((a, b) => {
          const dateA = new Date(a.paid_on);
          const dateB = new Date(b.paid_on);
          if (dateA > dateB) return -1;
          if (dateA < dateB) return 1;

          // If paid_on dates are equal, sort by created_at
          const createdA = new Date(a.created_at);
          const createdB = new Date(b.created_at);
          return createdB - createdA;
        });

        setAllPayments(paymentsData);

        // Calculate total paid amount excluding the current payment if editing
        let paid = paymentsData.reduce((sum, p) => {
          if (paymentid && p.id == paymentid) {
            return sum; // Skip the current payment being edited
          }
          return sum + parseFloat(p.paid_amount || 0);
        }, 0);

        setTotalPaid(paid);
      } catch (error) {
        console.error("Error loading payment data", error);
        setError("Failed to load payment data");
      } finally {
        setLoading(false);
      }
    };

    fetchLeadAndPayments();
  }, [leadid, paymentid]);


  useEffect(() => {
    // Fetch existing payment if editing
    const fetchPayment = async () => {
      if (paymentid) {
        try {
          setLoading(true);
          const res = await fetch(`${baseURL}/api/payments/${paymentid}`);
          if (!res.ok) throw new Error("Failed to fetch payment");
          const data = await res.json();

          // Sort all payments by paid_on (descending) and then by created_at (descending)
          const sortedPayments = [...allPayments].sort((a, b) => {
            const dateA = new Date(a.paid_on);
            const dateB = new Date(b.paid_on);
            if (dateA > dateB) return -1;
            if (dateA < dateB) return 1;

            // If paid_on dates are equal, sort by created_at (oldest first)
            return new Date(a.created_at) - new Date(b.created_at);
          });

          // Find the index of current payment in the sorted array
          const currentPaymentIndex = sortedPayments.findIndex(p => p.id == paymentid);

          // Get all payments that were made BEFORE this one (after in the array since it's sorted newest first)
          const previousPayments = sortedPayments.slice(currentPaymentIndex + 1);

          const sumPreviousPayments = previousPayments.reduce(
            (sum, p) => sum + parseFloat(p.paid_amount || 0),
            0
          );

          // Calculate balance based on previous payments + current amount
          const newBalance = totalAmount - (sumPreviousPayments + parseFloat(data.paid_amount || 0));

          setFormData({
            paidAmount: data.paid_amount,
            balanceAmount: newBalance,
            paidOn: data.paid_on,
            remarks: data.remarks,
          });

        } catch (err) {
          console.error("Edit load failed", err);
          setError("Could not load payment for editing.");
        } finally {
          setLoading(false);
        }
      }
    };

    if (allPayments.length > 0) {
      fetchPayment();
    }
  }, [paymentid, totalAmount, allPayments]);


  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "paidAmount") {
      if (paymentid) {
        // Sort payments by paid_on and then created_at
        const sortedPayments = [...allPayments].sort((a, b) => {
          const dateA = new Date(a.paid_on);
          const dateB = new Date(b.paid_on);
          if (dateA > dateB) return -1;
          if (dateA < dateB) return 1;
          return new Date(a.created_at) - new Date(b.created_at);
        });

        const currentPaymentIndex = sortedPayments.findIndex(p => p.id == paymentid);
        const previousPayments = sortedPayments.slice(currentPaymentIndex + 1);
        const sumPreviousPayments = previousPayments.reduce(
          (sum, p) => sum + parseFloat(p.paid_amount || 0),
          0
        );

        const newPaidAmount = parseFloat(value) || 0;
        const newBalance = totalAmount - (sumPreviousPayments + newPaidAmount);

        setFormData(prev => ({
          ...prev,
          [name]: value,
          balanceAmount: newBalance
        }));
      } else {
        // In add mode, use the original logic
        const newPaidAmount = parseFloat(value) || 0;
        const updatedTotalPaid = totalPaid + newPaidAmount;
        const remaining = totalAmount - updatedTotalPaid;

        setFormData(prev => ({
          ...prev,
          [name]: value,
          balanceAmount: remaining
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Calculate final balance including this payment
    let finalBalance;
    let finalPaidAmount = parseFloat(formData.paidAmount) || 0;

    if (paymentid) {
      // For edit mode: total - (previous payments + current amount)
      const currentPaymentIndex = allPayments.findIndex(p => p.id == paymentid);
      const previousPayments = allPayments.slice(currentPaymentIndex + 1);
      const sumPreviousPayments = previousPayments.reduce((sum, p) => sum + parseFloat(p.paid_amount || 0), 0);
      finalBalance = totalAmount - (sumPreviousPayments + finalPaidAmount);
    } else {
      // For add mode: total - (all existing payments + new payment)
      finalBalance = totalAmount - (totalPaid + finalPaidAmount);
    }

    const dataToSend = {
      leadid,
      total_amount: totalAmount,
      paid_amount: finalPaidAmount,
      balance_amount: finalBalance,
      paid_on: formData.paidOn,
      remarks: formData.remarks,
      userid: userId,
      username: userName,
      status: "Pending",
    };

    try {
      const url = paymentid
        ? `${baseURL}/api/payments/${leadid}/${paymentid}`
        : `${baseURL}/api/payments`;

      const method = paymentid ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        navigate(`/s-payments/${leadid}`, {
          state: {
            success: paymentid
              ? "Payment updated successfully"
              : "Payment saved successfully",
          },
        });
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Error saving payment");
      }
    } catch (err) {
      console.error(err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="salesViewLeadsContainer">
      <Navbar onToggleSidebar={setCollapsed} />
      <div className={`salesViewLeads ${collapsed ? "collapsed" : ""}`}>
        <div className="addpayment-form-container">
          <h2 className="addpayment-form-header" style={{ "--theme-color": themeColor }}>
            {paymentid ? `Edit Receivables - ID: ${paymentid}` : `Add Receivables - Lead ID: ${leadid}`}
          </h2>

          {error && <div className="alert alert-danger">{error}</div>}

          <div
            className="payment-summary"
            style={{
              margin: "20px 0",
              backgroundColor: "#f9f9f9",
              border: "1px solid #ddd",
              padding: "15px",
              borderRadius: "8px",
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",

            }}
          >
            <p style={{ margin: 0 }}>
              <strong>Total Amount:</strong> ₹{totalAmount.toLocaleString()}
            </p>
            <p style={{ margin: 0 }}>
              <strong>Total Received (excluding current):</strong> ₹{totalPaid.toLocaleString()}
            </p>
            <p style={{ margin: 0 }}>
              <strong>Remaining:</strong>{" "}
              <span style={{ color: totalAmount - totalPaid > 0 ? "red" : "green" }}>
                ₹{(totalAmount - totalPaid).toLocaleString()}
              </span>
            </p>
          </div>



          <form onSubmit={handleSubmit} className="addpayment-form">
            <div className="addpayment-form-grid">
              <div className="addpayment-input-group">
                <label>
                  Received Amount<span style={{ color: "red" }}> *</span>
                </label>
                <input
                  type="number"
                  name="paidAmount"
                  placeholder="Enter amount"
                  value={formData.paidAmount}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="addpayment-input-group">
                <label>Balance After This Payment</label>
                <input
                  type="text"
                  name="balanceAmount"
                  value={formData.balanceAmount}
                  readOnly
                  className="readonly-input"
                />
              </div>

              <div className="addpayment-input-group">
                <label>
                  Received On<span style={{ color: "red" }}> *</span>
                </label>
                <input
                  type="date"
                  name="paidOn"
                  value={formData.paidOn}
                  onChange={handleChange}
                  required
                  max={today}
                />
              </div>

              <div className="addpayment-input-group full-width">
                <label>Remarks</label>
                <textarea
                  name="remarks"
                  placeholder="Enter remarks (optional)"
                  value={formData.remarks}
                  onChange={handleChange}
                  rows="3"
                />
              </div>
            </div>

            <div className="addpayment-form-footer">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                Back
              </button>
              <button className="btn btn-primary" type="submit" disabled={loading}>
                {loading
                  ? paymentid
                    ? "Updating..."
                    : "Saving..."
                  : paymentid
                    ? "Update Receivable"
                    : "Save Receivable"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default S_AddPaymentPage;